import { containsFuzzyPhrase, tokenize } from './fuzzyMatch';
import { queriesFor } from './knowledgeRegistry';
import type { AssistantAudience } from './assistantCapabilities';

/**
 * "What can you do?" — handled as a first-class question rather than a failure.
 *
 * The full capability list used to be reachable only by asking something the
 * assistant could not answer, which put a wall of text in front of the user at
 * the exact moment they wanted a short apology, and made the list undiscoverable
 * for anyone who happened to guess right first time. Splitting the two lets the
 * decline stay one line and the list stay complete.
 */

const HELP_PHRASES = [
  'what can you do',
  'what can you help with',
  'what can i ask',
  'what can you answer',
  'what do you do',
  'what questions can you answer',
  'what are you able to do',
  'show me what you can do',
  'list what you can do',
  'what else can you do',
  'your capabilities',
];

/**
 * Openers that mean "show me the menu" on their own and nothing else.
 *
 * Matched whole rather than as contained phrases. "help me" is a help request
 * by itself and the opening of an instruction — "help me set the capacity on
 * Cheyenne" — everywhere else, and answering that with a menu would drop a
 * change the user actually wanted.
 */
const EXACT_HELP = ['help', 'help me', 'halp', 'commands', 'options'];

/** True when the message is asking what the assistant can do. */
export const isHelpRequest = (message: string): boolean => {
  const normalised = message.trim().toLowerCase().replace(/[?.!]+$/, '').trim();
  if (EXACT_HELP.includes(normalised)) return true;
  const tokens = tokenize(normalised);
  return HELP_PHRASES.some((phrase) => containsFuzzyPhrase(tokens, tokenize(phrase)));
};

const bullet = (line: string): string => `\u2022 "${line}"`;

export interface CapabilityReplyOptions {
  audience: AssistantAudience;
  canEditLocations: boolean;
  /** Concrete edit examples, already naming one of this account's locations. */
  editExamples: string[];
}

/**
 * The full "here is what I can do" reply.
 *
 * Built from the registry rather than a hand-kept list, so a skill added
 * tomorrow appears here without anyone remembering to update copy.
 */
export const buildCapabilityReply = ({
  audience,
  canEditLocations,
  editExamples,
}: CapabilityReplyOptions): string => {
  const questions = queriesFor(audience).map((query) => query.example);
  const sections: string[] = [];

  if (questions.length > 0) {
    sections.push(`Questions I can answer:\n${questions.map(bullet).join('\n')}`);
  }

  if (canEditLocations && editExamples.length > 0) {
    sections.push(`Changes I can make:\n${editExamples.map(bullet).join('\n')}`);
  }

  if (sections.length === 0) {
    return 'I cannot do anything for this account yet \u2014 I am still learning.';
  }

  return `Here is what I can do right now.\n\n${sections.join('\n\n')}`;
};
